<template>
  <ClientOnly>
    <div class="product-gallery" v-resize="handleResize">
      <h3 class="product-gallery__title">{{ title }} ({{ (resources || []).length }})</h3>
      <div class="product-gallery__inner">
        <div
          class="product-gallery__item"
          v-for="(image, index) in props.resources"
          :key="image.fileId || image.fileName || index"
          v-show="index < imgNum"
        >
          <div class="product-gallery__item-inner" @click="openCarousel(index)">
            <img
              :src="baseUrlMedia + image.fileId + '/' + image.fileName + imgRatio"
              :alt="setProperFieldExternalFirst(image, 'title') || transformFilename(image.fileName) || 'image'"
              class="product-gallery__image"
              @load="onImgLoad"
            />
          </div>
        </div>
        <div v-if="isShowMoreVisible" class="product-gallery__item">
          <div class="product-gallery__item-inner product-gallery__item-inner_more" @click="openCarousel(0)">
            {{ more }} ({{ (resources || []).length }})
          </div>
        </div>
      </div>

      <!-- Modal for ProductCarousel -->
      <teleport to="body">
        <ClientOnly>
          <div v-if="showModal" class="product-gallery__modal-overlay" @click.self="closeModal">
            <ProductCarousel
              :resources="props.resources"
              :isModalType="true"
              :activeSlide="modalIndex"
              :title="props.title"
              @close="closeModal"
            />
          </div>
        </ClientOnly>
      </teleport>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
  import { useMainStore } from '@/stores/main';

  // Type definitions for gallery component
  interface GalleryResource {
    fileId?: string;
    fileName?: string;
    external?: {
      title?: string;
      [key: string]: any;
    };
    internal?: {
      title?: string;
      [key: string]: any;
    };
  }

  const emit = defineEmits<{
    imageLoaded: [];
  }>();

  // Local helper (mirrors previous filter util)
  function setProperFieldExternalFirst(value: any, field: string): string {
    if (!value) return '';
    return value.external && value.external[field]
      ? value.external[field]
      : value.internal && value.internal[field]
        ? value.internal[field]
        : '';
  }

  function transformFilename(filename: string | undefined): string {
    if (!filename) return '';
    return filename
      .replace(/\.[^/.]+$/, '') // remove file extension
      .replace(/_/g, ' '); // replace underscores with spaces
  }

  const props = defineProps<{
    title?: string;
    more?: string;
    resources?: GalleryResource[];
  }>();

  const mainStore = useMainStore();
  const baseUrlMedia = computed(() => mainStore.baseUrlMedia);

  // Use a stable default so SSR and client initial render match; adjusted onMounted
  const imgNum = ref(3);
  const imgRatio = '?width=405&height=228';

  const isShowMoreVisible = computed(() => (props.resources?.length || 0) > imgNum.value);

  // Modal state
  const showModal = ref(false);
  const modalIndex = ref(0);

  function openCarousel(index: number): void {
    modalIndex.value = index;
    showModal.value = true;
  }

  function closeModal(): void {
    showModal.value = false;
  }

  // Watch modal visibility to toggle body scroll locking using store
  watch(
    () => showModal.value,
    (val) => {
      if (val) {
        mainStore.lockBodyScroll();
      } else {
        mainStore.unlockBodyScroll();
      }
    }
  );

  function setImgNum(width: number): void {
    if (width > 767) {
      if (imgNum.value !== 7) imgNum.value = 7;
    } else if (width > 680) {
      if (imgNum.value !== 5) imgNum.value = 5;
    } else {
      if (imgNum.value !== 3) imgNum.value = 3;
    }
  }

  function handleResize(): void {
    if (!import.meta.env.SSR) {
      const width = window.innerWidth;
      setImgNum(width);
    }
  }

  const isMounted = ref(false);
  function onImgLoad(): void {
    if (!isMounted.value) return;
    emit('imageLoaded');
  }

  onMounted(() => {
    const width = window.innerWidth;
    setImgNum(width);
    isMounted.value = true;
  });

  onBeforeUnmount(() => {
    // Ensure body is unlocked if component unmounts while modal is open
    if (showModal.value) {
      mainStore.unlockBodyScroll();
    }
  });
</script>
